import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { useHistory } from 'react-router-dom'
import { animateScroll as scroll } from 'react-scroll'

import { GoalWizardNavbar, GoalWizardStepper } from 'components/GoalWizard'
import MonthStep from './components/MonthStep'
import CommissionStep from './components/CommissionStep'
import ValuesStep from './components/ValuesStep'
import GoalStep from './components/GoalStep'
import ReviewStep from './components/ReviewStep'
import BulkGoalStepActions from './components/BulkGoalStepActions'
import BulkGoalsSideBar from './components/BulkGoalsSideBar'
import CreationResultDialog from './components/CreationResultDialog'
import { useBulkGoalStores } from './hooks/useBulkGoalStores'
import { useBulkGoalSubmission } from './hooks/useBulkGoalSubmission'
import { PreparedStoreGoalConfigs, useStoreGoalConfigs } from './hooks/useStoreGoalConfigs'
import { generateMonthOptions, getManualGoalTotals } from './service'
import useBulkGoalStyles from './styles'
import { DailyGoalMode, DailyGoalValues } from './types'
import { isStoreCommissionFilled, isStoreGoalsFilled, isStoreValuesFilled } from './utils'

const BulkGoals = () => {
  const classes = useBulkGoalStyles()
  const history = useHistory()
  const monthOptions = useMemo(() => generateMonthOptions(), [])
  const monthLabelsByValue = useMemo(
    () => new Map(monthOptions.map(month => [month.value, month.label])),
    [monthOptions]
  )
  const [activeStep, setActiveStep] = useState(0)
  const [contentStep, setContentStep] = useState(0)
  const [progressStep, setProgressStep] = useState(0)
  const [isAdvancingStep, setIsAdvancingStep] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState('')
  const [dailyGoalMode, setDailyGoalMode] = useState<DailyGoalMode>(null)
  const [dailyGoalValues, setDailyGoalValues] = useState<DailyGoalValues>({})
  const [isValuesSetupComplete, setIsValuesSetupComplete] = useState(false)
  const transitionTokenRef = useRef(0)
  const transitionFrameRefs = useRef<number[]>([])
  const transitionTimeoutRef = useRef<number | null>(null)
  const selectedMonthLabel = monthLabelsByValue.get(selectedMonth) || '-'

  const stores = useBulkGoalStores({ selectedMonth })
  const configs = useStoreGoalConfigs({
    selectedAvailableStores: stores.selectedAvailableStores,
    selectedMonth
  })

  const allStoreCommissionFilled = configs.storeGoalConfigs.length > 0 && configs.storeGoalConfigs.every(isStoreCommissionFilled)
  const allStoreValuesFilled = configs.storeGoalConfigs.length > 0 && configs.storeGoalConfigs.every(isStoreValuesFilled)
  const allStoreGoalsFilled = configs.storeGoalConfigs.length > 0 && configs.storeGoalConfigs.every(isStoreGoalsFilled)
  const submission = useBulkGoalSubmission({
    allStoreCommissionFilled,
    allStoreGoalsFilled,
    allStoreValuesFilled,
    dailyGoalMode,
    dailyGoalValues,
    selectedAvailableStores: stores.selectedAvailableStores,
    selectedMonth,
    selectedStoreIds: stores.selectedStoreIds,
    storeGoalConfigsByStoreId: configs.storeGoalConfigsByStoreId
  })
  const { setCreationResults } = submission

  const stepCanProceed = [
    Boolean(
      selectedMonth &&
      stores.selectedAvailableStores.length > 0 &&
      !stores.storesError &&
      !stores.storesLoading &&
      !stores.groupsLoading &&
      !stores.isCheckingAvailability
    ),
    allStoreCommissionFilled,
    allStoreValuesFilled && isValuesSetupComplete,
    allStoreGoalsFilled
  ]
  const firstInvalidStep = stepCanProceed.findIndex(canProceed => !canProceed)
  const lowestInvalidStep = firstInvalidStep === -1 ? stepCanProceed.length : firstInvalidStep
  const maxValidStep = Math.min(progressStep, lowestInvalidStep)
  const failedCreationResults = submission.creationResults.filter(result => !result.success)
  const activeStepCanProceed = activeStep === 4 ? stepCanProceed.every(Boolean) : stepCanProceed[activeStep]
  const canGoNext = activeStepCanProceed && !isAdvancingStep
  const isCheckingCurrentStep = activeStep === 0 && (
    stores.storesLoading || stores.groupsLoading || stores.isCheckingAvailability
  )
  const nextStepDisabledReason = activeStepCanProceed || isCheckingCurrentStep
    ? undefined
    : stores.storesError && activeStep === 0
      ? stores.storesError
      : 'Preencha todos os campos obrigatórios corretamente para continuar.'
  const exitToDashboard = () => history.push('/stores')

  const cancelPendingStepTransition = useCallback((options: { resetAdvancing?: boolean } = {}) => {
    transitionTokenRef.current += 1
    transitionFrameRefs.current.forEach(frameId => window.cancelAnimationFrame(frameId))
    transitionFrameRefs.current = []
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
    }
    if (options.resetAdvancing) setIsAdvancingStep(false)
  }, [])

  useEffect(() => {
    setCreationResults([])
  }, [selectedMonth, stores.selectedStores, configs.storeGoalConfigs, setCreationResults])

  useEffect(() => {
    setProgressStep(step => Math.min(step, maxValidStep))
  }, [maxValidStep])

  useEffect(() => {
    setActiveStep(0)
    setContentStep(0)
    setProgressStep(0)
    setDailyGoalMode(null)
    setDailyGoalValues({})
    setIsValuesSetupComplete(false)
    cancelPendingStepTransition({ resetAdvancing: true })
  }, [cancelPendingStepTransition, selectedMonth, stores.selectedStores])

  useEffect(() => () => cancelPendingStepTransition(), [cancelPendingStepTransition])

  const handleMonthChange = (event: ChangeEvent<{ name?: string; value: unknown }>) => {
    setSelectedMonth(event.target.value as string)
    stores.setSelectedStores([])
  }

  const handleManualGoalsComplete = (values: DailyGoalValues) => {
    const totalsByStoreId = getManualGoalTotals(
      values,
      stores.selectedAvailableStores.map(store => store._id)
    )
    totalsByStoreId.forEach((total, storeId) => {
      configs.handleValuesTableChange(storeId, 'total', total)
    })
  }

  const navigateToReachableStep = (index: number) => {
    if (index > maxValidStep) return
    cancelPendingStepTransition({ resetAdvancing: true })
    const transitionToken = transitionTokenRef.current
    setActiveStep(index)
    scroll.scrollToTop()
    scheduleFrame(() => {
      if (transitionTokenRef.current !== transitionToken) return
      setContentStep(index)
    })
  }

  const handleStepClick = (index: number) => {
    navigateToReachableStep(index)
  }

  const scheduleFrame = (callback: () => void) => {
    const frameId = window.requestAnimationFrame(callback)
    transitionFrameRefs.current.push(frameId)
    return frameId
  }

  const renderStepAfterPaint = (nextStep: number, transitionToken: number, preparedConfigs?: PreparedStoreGoalConfigs | null) => {
    scheduleFrame(() => {
      if (transitionTokenRef.current !== transitionToken) return
      scroll.scrollToTop()
      scheduleFrame(() => {
        if (transitionTokenRef.current !== transitionToken) return
        if (!preparedConfigs) {
          unstable_batchedUpdates(() => {
            setContentStep(nextStep)
            setIsAdvancingStep(false)
          })
          return
        }

        transitionTimeoutRef.current = window.setTimeout(() => {
          transitionTimeoutRef.current = null
          if (transitionTokenRef.current !== transitionToken) return
          const committedPreparedConfigs = configs.commitStoreGoalConfigs(preparedConfigs)
          if (!committedPreparedConfigs) {
            setIsAdvancingStep(false)
            return
          }
          unstable_batchedUpdates(() => {
            setProgressStep(step => Math.max(step, nextStep))
            setActiveStep(nextStep)
            setContentStep(nextStep)
            setIsAdvancingStep(false)
          })
        }, 0)
      })
    })
  }

  const completeStepAdvance = (nextStep: number, transitionToken: number, preparedConfigs?: PreparedStoreGoalConfigs | null) => {
    if (preparedConfigs) {
      renderStepAfterPaint(nextStep, transitionToken, preparedConfigs)
      return
    }

    unstable_batchedUpdates(() => {
      setProgressStep(step => Math.max(step, nextStep))
      setActiveStep(nextStep)
    })
    renderStepAfterPaint(nextStep, transitionToken, preparedConfigs)
  }

  const goNext = async () => {
    if (activeStep === 0) stores.setHasLeftStep0(true)
    if (activeStep === 4) {
      submission.handleCreateBulkGoals()
      return
    }

    const nextStep = activeStep + 1

    // Steps already reachable through the stepper were prepared before.
    // Navigate only so returning users do not reload defaults over manual edits.
    if (nextStep <= maxValidStep) {
      navigateToReachableStep(nextStep)
      return
    }

    cancelPendingStepTransition()
    const transitionToken = transitionTokenRef.current
    setIsAdvancingStep(true)
    let completedAdvance = false
    const advanceToStep = (nextStep: number, preparedConfigs?: PreparedStoreGoalConfigs | null) => {
      completedAdvance = true
      completeStepAdvance(nextStep, transitionToken, preparedConfigs)
    }

    try {
      if (activeStep === 0) {
        const commissionFilled = await configs.loadCommissionDefaults()
        if (commissionFilled === null) return
        if (commissionFilled) {
          const valuesFilled = await configs.loadValueDefaults()
          if (valuesFilled === null) return
          advanceToStep(2)
          return
        }

        advanceToStep(1)
        return
      }

      if (activeStep === 1) {
        const valuesFilled = await configs.loadValueDefaults()
        if (valuesFilled === null) return
        advanceToStep(2)
        return
      }

      if (activeStep === 2) {
        const preparedConfigs = await configs.prepareSellerGoalConfigs()
        if (!preparedConfigs) return
        advanceToStep(3, preparedConfigs)
        return
      }

      advanceToStep(4)
    } finally {
      if (!completedAdvance) setIsAdvancingStep(false)
    }
  }

  const renderStep = () => {
    if (contentStep === 0) return renderMonthStep()
    if (contentStep === 1) return renderCommissionStep()
    if (contentStep === 2) return renderValuesStep()
    if (contentStep === 3) return renderGoalStep()
    return renderReviewStep()
  }

  const renderMonthStep = () => <MonthStep {...stores} classes={classes} monthOptions={monthOptions} selectedMonth={selectedMonth} onMonthChange={handleMonthChange} />
  const renderCommissionStep = () => <CommissionStep classes={classes} commissionAutofillStatus={configs.commissionAutofillStatus} values={configs.commissionTableValues} onApplyToAll={configs.handleCommissionApplyToAll} onChange={configs.handleCommissionTableChange} />
  const renderValuesStep = () => <ValuesStep classes={classes} dailyGoalMode={dailyGoalMode} dailyGoalValues={dailyGoalValues} isSetupComplete={isValuesSetupComplete} selectedMonth={selectedMonth} selectedMonthLabel={selectedMonthLabel} values={configs.remunerationTableValues} valuesAutofillStatus={configs.valuesAutofillStatus} onApplyToAll={configs.handleValuesApplyToAll} onChange={configs.handleValuesTableChange} onDailyGoalModeChange={setDailyGoalMode} onDailyGoalValuesChange={setDailyGoalValues} onManualGoalsComplete={handleManualGoalsComplete} onResetValues={configs.resetValueDefaults} onSetupCompleteChange={setIsValuesSetupComplete} />
  const renderGoalStep = () => <GoalStep classes={classes} {...configs} />
  const renderReviewStep = () => <ReviewStep classes={classes} selectedAvailableStores={stores.selectedAvailableStores} selectedMonthLabel={selectedMonthLabel} storeGoalConfigs={configs.storeGoalConfigs} />

  const totalSelectedSellers = configs.storeGoalConfigs.length > 0
    ? configs.storeGoalConfigs.reduce((acc, config) => acc + config.usersWorking.filter(user => user.active).length, 0)
    : stores.selectedAvailableStores.reduce((acc, store) => acc + store.sellers, 0)

  return (
    <div className={classes.main}>
      <div className={classes.navbar}>
        <GoalWizardNavbar title='Cadastrar metas do mês' context='| múltiplas lojas' canSave={false} isSaved={progressStep > activeStep} loading={submission.isCreatingGoals} savedMessage='Informações preenchidas' onLogoClick={exitToDashboard} onExit={exitToDashboard} />
        <GoalWizardStepper activeStep={activeStep} progressStep={maxValidStep} onStepClick={handleStepClick} />
      </div>

      {activeStep < 4 && (
        <div className={classes.sideBar}>
          <BulkGoalsSideBar allStoreCommissionFilled={allStoreCommissionFilled} allStoreGoalsFilled={allStoreGoalsFilled} allStoreValuesFilled={allStoreValuesFilled} classes={classes} selectedMonth={selectedMonth} selectedMonthLabel={selectedMonthLabel} selectedStoresCount={stores.selectedAvailableStores.length} storeGoalConfigsCount={configs.storeGoalConfigs.length} totalSelectedSellers={totalSelectedSellers} />
        </div>
      )}

      <div className={classes.contentArea}>{renderStep()}</div>

      {(activeStep !== 2 || isValuesSetupComplete) && (
        <BulkGoalStepActions activeStep={activeStep} canGoNext={canGoNext} classes={classes} disabledReason={nextStepDisabledReason} hasCreatedGoals={submission.creationResults.length > 0} isAdvancingStep={isAdvancingStep} isCreatingGoals={submission.isCreatingGoals} selectedStoresCount={stores.selectedAvailableStores.length} onNext={goNext} />
      )}

      <CreationResultDialog classes={classes} failedResults={failedCreationResults} hasErrors={failedCreationResults.length > 0} isCreatingGoals={submission.isCreatingGoals} open={submission.isCreationDialogOpen} onClose={() => history.push('/stores')} />
    </div>
  )
}

export default BulkGoals
